/* The bonus stage's board, persisted with progress.
 *
 * The artifact kept the board in a module-level `G` and re-rendered cells by
 * rebuilding innerHTML. Here the board lives in the same store as everything
 * else, so a reload resumes the game exactly where it stopped — which is what
 * the artifact promised and only partly delivered.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GOPAL, MAXV, MERGE } from '../data/ladder';
import { gCanMove, gMove, gNew, gPeak, gSpawn, type Dir } from '../engine/game';
import { gLevel } from '../engine/game';
import { useApp } from './AppProvider';
import type { Block, SavedGame } from '../types';

const EMPTY_BOARD = new Array<number>(16).fill(0);

export interface GameStage {
  board: number[];
  score: number;
  best: number;
  moves: number;
  peak: number;
  over: boolean;
  won: boolean;
  /** the caption under the board: the last merge, or why the board stopped */
  caption: string | null;
  /** the cell that just appeared, for the pop-in */
  newTile: number;
  play: (dir: Dir) => void;
  reset: () => void;
}

export function useGameStage(block: Block | null): GameStage {
  const { progress, setGameState, notify, completeBlock } = useApp();
  const { t } = useTranslation();
  const saved = progress.game;
  const [caption, setCaption] = useState<string | null>(null);
  const [newTile, setNewTile] = useState(-1);

  /* A learner who never opens the bonus stage never pays for a random board. */
  useEffect(() => {
    if (!saved?.board || saved.board.length !== 16) {
      setGameState({ board: gNew(), score: 0, best: 0, moves: 0, peak: 0, won: false });
    }
  }, [saved, setGameState]);

  const board = saved?.board?.length === 16 ? saved.board : EMPTY_BOARD;
  const score = saved?.score ?? 0;
  const moves = saved?.moves ?? 0;
  const won = saved?.won ?? false;

  const peak = useMemo(() => Math.max(saved?.peak ?? 0, gPeak(board)), [board, saved]);
  const over = useMemo(() => !gCanMove(board), [board]);
  const best = Math.max(saved?.best ?? 0, score);

  const persist = useCallback(
    (next: SavedGame) => setGameState(next),
    [setGameState],
  );

  const play = useCallback(
    (dir: Dir) => {
      if (!block || over) return;
      const result = gMove(board, dir);
      if (!result.moved) {
        /* the top tile keeps both of itself: an event is where the pipeline ends */
        if (result.blocked) setCaption(t(GOPAL));
        return;
      }
      const nextBoard = gSpawn(result.board);
      const nextScore = score + result.gained;
      const topMerge = result.merges.length ? result.merges[result.merges.length - 1] : 0;
      const mergeKey = topMerge ? MERGE[String(gLevel(topMerge))] : undefined;
      if (mergeKey) setCaption(t(mergeKey));
      setNewTile(result.at.length ? result.at[result.at.length - 1] : -1);

      const justWon = !won && result.top >= MAXV;
      persist({
        board: nextBoard,
        score: nextScore,
        best: Math.max(best, nextScore),
        moves: moves + 1,
        peak: gPeak(nextBoard),
        won: won || justWon,
      });

      if (justWon && !progress.done[block.id]) {
        completeBlock(block.id);
        notify(`${t('gameWonXp')} · +${block.xp} ${t('xp')}`);
      }
    },
    [best, block, board, completeBlock, moves, notify, over, persist, progress.done, score, t, won],
  );

  const reset = useCallback(() => {
    setCaption(null);
    setNewTile(-1);
    persist({ board: gNew(), score: 0, best, moves: 0, peak: 0, won: false });
  }, [best, persist]);

  return { board, score, best, moves, peak, over, won, caption, newTile, play, reset };
}
